import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import Layout from "@/components/Layout";
import { supabase } from "@/integrations/supabase/client";
import { Download } from "lucide-react";

interface Framework {
  id: string;
  title: string;
  description: string;
  cover_image_url?: string;
  file_url?: string;
  slug: string;
  content?: string;
}

const TemplatesAndFrameworks = () => {
  const [frameworks, setFrameworks] = useState<Framework[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFrameworks();
  }, []);

  const loadFrameworks = async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from("frameworks")
        .select("*")
        .order("created_at", { ascending: false });

      if (data) setFrameworks(data);
    } catch (error) {
      console.error("Error loading frameworks:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="py-8 md:py-12">
        <div className="container-modern">
          <div className="text-center mb-8 md:mb-12 px-4">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">Templates & Frameworks</h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              Strategic frameworks and templates to help you structure your business processes and decision-making.
            </p>
          </div>

          {loading ? (
            <Card className="p-6 md:p-8 text-center mx-4">
              <p className="text-muted-foreground">Loading frameworks...</p>
            </Card>
          ) : frameworks.length > 0 ? (
            <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 px-4">
              {frameworks.map((framework) => (
                <Card key={framework.id} className="hover:shadow-lg transition-shadow">
                  {framework.cover_image_url && (
                    <div className="aspect-video bg-muted rounded-t-lg overflow-hidden">
                      <img 
                        src={framework.cover_image_url} 
                        alt={framework.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <CardHeader>
                    <CardTitle className="text-lg">{framework.title}</CardTitle>
                    <CardDescription>{framework.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button asChild className="w-full">
                      <Link to={`/resources/frameworks/${framework.slug}`}>
                        See More
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
            ) : (
            <Card className="p-6 md:p-8 text-center mx-4">
              <p className="text-muted-foreground">No templates or frameworks available yet. Check back soon!</p>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default TemplatesAndFrameworks;